import { useEffect, useRef, useState } from 'react'
import { acceptConsent, getStoredConsent, rejectConsent } from '../lib/analytics'

/**
 * Consent Mode v2 banner. Renders only after mount (avoids SSR/hydration
 * mismatch) and only when the visitor has not yet recorded a choice. The
 * default consent state is set by the bootstrap script in __root.tsx — this
 * just lets the visitor change it.
 */
export function ConsentBanner() {
  const [visible, setVisible] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (getStoredConsent() !== null) return
    setVisible(true)
    document.body.dataset.consentPending = 'true'
    return () => {
      delete document.body.dataset.consentPending
    }
  }, [])

  useEffect(() => {
    const banner = bannerRef.current
    if (!visible || !banner) return
    // Full-screen surfaces reserve the banner's actual height, including text
    // wrapping, zoom and the safe area. Consent choices remain equally usable.
    const update = () => document.documentElement.style.setProperty(
      '--consent-banner-height', `${Math.ceil(banner.getBoundingClientRect().height)}px`,
    )
    update()
    const observer = new ResizeObserver(update)
    observer.observe(banner)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--consent-banner-height')
    }
  }, [visible])

  if (!visible) return null

  const accept = () => {
    acceptConsent()
    delete document.body.dataset.consentPending
    setVisible(false)
  }

  const reject = () => {
    rejectConsent()
    delete document.body.dataset.consentPending
    setVisible(false)
  }

  return (
    <div ref={bannerRef} className="fixed inset-x-0 bottom-0 z-[200] px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-4 sm:pb-4">
      <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-3 shadow-2xl max-[359px]:flex max-[359px]:items-center max-[359px]:gap-2 max-[359px]:p-2 sm:flex sm:items-center sm:gap-4 sm:rounded-2xl sm:p-5">
        <p className="mb-2 text-xs leading-snug text-slate-600 max-[359px]:mb-0 max-[359px]:flex-1 max-[359px]:text-[11px] sm:mb-0 sm:flex-1 sm:text-sm sm:leading-relaxed">
          Kami memakai cookie untuk analitik situs.{' '}
          Lihat{' '}
          <a href="/privasi" className="font-semibold text-blue-600 hover:text-blue-700">
            Kebijakan Privasi
          </a>
          .
        </p>
        <div className="flex flex-shrink-0 gap-2 max-[359px]:gap-1">
          <button
            type="button"
            onClick={reject}
            className="min-h-11 flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 max-[359px]:w-12 max-[359px]:px-1 max-[359px]:text-xs sm:flex-none"
          >
            Tolak
          </button>
          <button
            type="button"
            onClick={accept}
            className="min-h-11 flex-1 rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-blue-700 max-[359px]:w-12 max-[359px]:px-1 max-[359px]:text-xs sm:flex-none"
          >
            Terima
          </button>
        </div>
      </div>
    </div>
  )
}
